import {
  HStack,
  PopoverTrigger,
  Popover,
  VStack,
  Icon,
  PopoverContent,
  Badge,
  Box,
  Text,
  IconButton,
  OrderedList,
  ListItem,
  Link,
  useDisclosure,
  Tooltip,
  Portal,
} from "@chakra-ui/react";
import { InfoOutlineIcon } from "@chakra-ui/icons";
import { BiCart, BiChevronDown, BiInfinite } from "react-icons/bi";
import { FiAlertTriangle, FiArrowDown, FiArrowRight, FiArrowUp } from "react-icons/fi";
import {
  formatDate,
  formatTime,
  findLatestETAWithGivenTime,
} from "../../../utils/dateResolvers";
import {
  epochPlatformEtaSeconds,
  getSupplierStateColor,
  OrderStatus,
} from "../../Orders/enums";
import GarrazCopyText from "../../Orders/components/GaraazCopyText";
import GaraazGeneralPopover from "../../Orders/components/GaraazGeneralPopover";
import GaraazPopOverContainerStack from "../../Orders/components/GaraazPopOverContainerStack";
import useOrdersStore from "../store/useOrdersStore";
import {
  AllocatedTo,
  finalOrcaStates,
  startOrcaStates,
} from "./GaraazOrderComponent";
import { useGetOrderMediaConfig } from "../../PlaceOrderTest/queryHooks";
import { isMobileConfigFlagEnabled } from "../../../utils/parseMobileConfigBooleanFlag";
import {
  useGetAllSuppliers,
  useGetCustomerAllSubscriptions,
} from "../../Orders/queryHooks";
import { formatSupplierAssociationsForDisplay } from "../../../utils/supplierAssociationUtils";
import { useEffect, useMemo, useState } from "react";
import GaraazCountDownClockV2 from "../../Orders/components/GaraazCountDownClockV2";
import GaraazCptSlotLabel from "../../Orders/components/GaraazCptSlotLabel";
import { pickOrderEtaMinGaraazSlot } from "../../Orders/utils/orderEtaGaraazSlot";
import { GiReturnArrow } from "react-icons/gi";
import { removeUnderScoreWithUpperCase } from "../../../utils/removeUnderscore";
import { FaTruckLoading } from "react-icons/fa";
import { BiDollarCircle } from "react-icons/bi";
import { FaCarSide } from "react-icons/fa";
import paths from "../../../utils/paths";
import { NavLink } from "react-router-dom";
import useHomeStore from "../../../useHomeStore";
import { formatInDateMonthYearUTC } from "../../../utils/dateResolvers";
import { EngagementMode } from "../../Orders/enums";
import GaraazGeneralPopoverV2 from "../../Orders/components/GaraazGeneralPopverV2";
import { AiOutlineStop } from "react-icons/ai";
import {
  formatMobileNumber,
  getNationalMobileForCopy,
} from "../../../utils/util";

const ALLOCATION_ACTION_ICONS: Record<
  string,
  { icon: typeof FiArrowDown; color: string; label: string }
> = {
  STOCK_IN: { icon: FiArrowDown, color: "#2563EB", label: "Stock In" },
  STOCK_OUT: { icon: FiArrowUp, color: "#EA580C", label: "Stock Out" },
  PROCESS: { icon: FiArrowRight, color: "#6B7280", label: "Process" },
};

type GaraazOrderComponentHeaderProps = {
  orderStatus: any;
  orderData: any;
  etaMissed: boolean;
  setEtaMissed: React.Dispatch<React.SetStateAction<boolean>>;
  activesupplierOrderStatus: AllocatedTo;
  findBrandName?: (brandId: string) => any;
  transactionType: any;
  getSupplierBusinessName: (supplierId: string) => any;
  isCustomerOrderFreezed: boolean;
  getSupplierBranchName: (supplierId: any, branchId: any) => any;
  getSupplierNumber: (supplierId: string, branchId?: string) => string;
  onOpenCptUpdate?: () => void;
};

const GaraazOrderComponentHeader: React.FC<GaraazOrderComponentHeaderProps> = ({
  etaMissed,
  setEtaMissed,
  orderStatus,
  orderData,
  activesupplierOrderStatus,
  transactionType,
  getSupplierBusinessName,
  isCustomerOrderFreezed,
  getSupplierBranchName,
  getSupplierNumber,
  onOpenCptUpdate,
}) => {
  const orderEtaFromStore = useOrdersStore(
    (state) => state.orderEtas[orderData._id],
  );
  const orderEta = useMemo(() => {
    const raw = orderEtaFromStore ?? orderData?.orderETA;
    return Array.isArray(raw) ? raw : [];
  }, [orderEtaFromStore, orderData?.orderETA]);
  const { data: mobileConfigs } = useGetOrderMediaConfig();
  const isCarIconVisible = isMobileConfigFlagEnabled(
    mobileConfigs?.ff_car_catalogue_icon,
    true,
  );
  const { data: allSubscriptions } = useGetCustomerAllSubscriptions(
    orderData.customer._id
  );
  const { data: allSuppliers } = useGetAllSuppliers();
  const [customerSupplierAssociations, setCustomerSupplierAssociations] =
    useState<{}[]>([]);
  const setScrollOrderId = useOrdersStore((state) => state.setScrollOrderId);
  const etaProvideClockValue0 = Math.floor(
    new Date(orderData.orderDate).getTime() / 1000
  );
  const etaProvideClockValue100 =
    Math.floor(new Date(orderData.orderDate).getTime() / 1000) +
    mobileConfigs?.threshold_giving_eta_to_customer_mins * 60;

  let orderNotoShow = orderData.orderNo.split("-");
  orderNotoShow = orderNotoShow[orderNotoShow.length - 1];

  const customerDetailsToCopy = `
     ${orderData?.customer?.name}
     ${orderData?.shipmentAddress?.addressLine1}
     ${orderData?.shipmentAddress?.addressLine2}
     ${orderData?.shipmentAddress?.city}, ${orderData?.shipmentAddress?.state
    }, ${orderData?.shipmentAddress?.pinCode}
     ${orderData?.shipmentAddress?.country}
     ${formatMobileNumber(orderData.customer?.mobileNumber)}
     ${orderData?.customer?.code}
  `;

  const [etaProvideMissed, setEtaProvideMissed] = useState(false);
  const latestETA = orderEta.length
    ? findLatestETAWithGivenTime(orderEta)?.latestTime
    : undefined;
  const cptGaraazSlot = useMemo(
    () => pickOrderEtaMinGaraazSlot(orderEta),
    [orderEta],
  );

  const engagementMode = (
    orderData?.customer?.engagementMode ?? ""
  ).toUpperCase();
  const isSubscribedEngagement =
    engagementMode === EngagementMode.SUBSCRIBED ||
    engagementMode === "SUBSCRIPTION";
  const engagementModeLabel = isSubscribedEngagement
    ? "Subscribed"
    : orderData?.customer?.engagementMode;

  const renderEngagementModeBadge = () =>
    orderData?.customer?.engagementMode ? (
      <Badge
        ml={0}
        backgroundColor={`${
          engagementMode === EngagementMode.PLATFORM
            ? "white"
            : engagementMode === EngagementMode.EXPIRED
              ? "red.500"
              : "#FFD601"
        }`}
        outline={
          engagementMode === EngagementMode.PLATFORM
            ? "1px solid #FFD601"
            : "none"
        }
        fontSize={"xx-small"}
        height={"fit-content"}
        flexShrink={0}
        color={
          engagementMode === EngagementMode.EXPIRED ? "white" : "black"
        }
      >
        {engagementMode === EngagementMode.EXPIRED ||
        isSubscribedEngagement ? (
          <GaraazGeneralPopover
            ArekeysVisible={true}
            title={
              isSubscribedEngagement
                ? "Subscription Details"
                : "Expired Subscription Details"
            }
            value={
              isSubscribedEngagement
                ? ([
                    getActiveSubscriptionDetails(allSubscriptions, "UI"),
                  ].filter(Boolean) as any)
                : ([
                    getExpiredSubscriptionDetails(allSubscriptions, "UI"),
                  ].filter(Boolean) as any)
            }
            CopierKeys={[]}
            titleIcon={null}
          >
            <PopoverTrigger>
              <Text
                fontWeight="bold"
                cursor={"pointer"}
                fontSize={"1em"}
                display={"inline"}
                justifyContent={"center"}
              >
                {engagementModeLabel}
              </Text>
            </PopoverTrigger>
          </GaraazGeneralPopover>
        ) : (
          engagementModeLabel
        )}
      </Badge>
    ) : null;

  const originalSub = useMemo(
    () => getActiveSubscriptionDetails(allSubscriptions, "ORIGINAL"),
    [allSubscriptions]
  );

  const isFreeDelivery = useMemo(() => {
    return (
      originalSub?.plan_deliveries > originalSub?.taken_deliveries
    );
  }, [originalSub]);

  useEffect(() => {
    setCustomerSupplierAssociations(
      formatSupplierAssociationsForDisplay(
        orderData?.customer?.supplierAssociations,
        allSuppliers,
      ),
    );
    setEtaProvideMissed(false);
  }, [orderData, allSuppliers]);

  function preparePopverAddressObject(shipmentAddress: any) {
    return {
      addressLine1: orderData?.shipmentAddress?.addressLine1,
      addressLine2: orderData?.shipmentAddress?.addressLine2,
      addressLine3:
        orderData?.shipmentAddress?.city +
        ", " +
        orderData?.shipmentAddress?.state +
        ", " +
        orderData?.shipmentAddress?.pinCode,
      country: orderData?.shipmentAddress?.country,
      "Mobile Number": formatMobileNumber(orderData.customer?.mobileNumber),
    };
  }

  function getActiveSubscriptionDetails(
    allSubscriptions: any,
    format: "UI" | "ORIGINAL"
  ) {
    if (!allSubscriptions) return;

    let activeSubscription;
    for (let i = 0; i < allSubscriptions.length; i++) {
      const endDate = new Date(allSubscriptions[i].endDate);
      const startDate = new Date(allSubscriptions[i].startDate);
      if (endDate >= new Date() && startDate <= new Date()) {
        activeSubscription = allSubscriptions[i];
        break;
      }
    }

    if (!activeSubscription) {
      return;
    }

    return format === "UI"
      ? {
        "Start Date": activeSubscription.startDate
          ? formatInDateMonthYearUTC(activeSubscription.startDate)
          : "",
        "End Date": activeSubscription.endDate
          ? formatInDateMonthYearUTC(activeSubscription.endDate)
          : "",
        Amount: "₹" + (activeSubscription.amount ?? ""),
        "Plan Deliveries":
          activeSubscription.plan_deliveries > 1000 ? (
            <BiInfinite></BiInfinite>
          ) : (
            activeSubscription.plan_deliveries
          ),
        "Taken Deliveries": activeSubscription.taken_deliveries ?? "",
      }
      : activeSubscription;
  }

  function getExpiredSubscriptionDetails(
    allSubscriptions: any,
    format: "UI" | "ORIGINAL"
  ) {
    if (!allSubscriptions) return;
    let maxEndDate = new Date("1970/01/01");
    let maxEndDateSubscription;

    for (let i = 0; i < allSubscriptions.length; i++) {
      const endDate = new Date(allSubscriptions[i].endDate);
      if (endDate > maxEndDate && endDate < new Date()) {
        maxEndDate = endDate;
        maxEndDateSubscription = allSubscriptions[i];
      }
    }

    if (!maxEndDateSubscription) {
      return;
    }

    return format === "UI"
      ? {
        "Start Date": maxEndDateSubscription.startDate
          ? formatInDateMonthYearUTC(maxEndDateSubscription.startDate)
          : "",
        "End Date": maxEndDateSubscription.endDate
          ? formatInDateMonthYearUTC(maxEndDateSubscription.endDate)
          : "",
        Amount: "₹" + (maxEndDateSubscription.amount ?? ""),
        "Plan Deliveries":
          maxEndDateSubscription.plan_deliveries > 1000 ? (
            <BiInfinite></BiInfinite>
          ) : (
            maxEndDateSubscription.plan_deliveries
          ),
        "Taken Deliveries": maxEndDateSubscription.taken_deliveries ?? "",
      }
      : maxEndDateSubscription;
  }

  async function onEtaProvideTimeComplete() {
    //These API calls from throwing ETA missed event Are supressed, as they are overloading BE
    // const response = await services.throwETAProvideMissedEvent(orderData._id);
  }

  async function onEtaTimeComplete() {
    //These API calls from throwing ETA missed event Are supressed, as they are overloading BE
    // const response = await services.throwETAMissedEvent(
    //   orderData._id,
    //   findLatestETAWithGivenTime(orderEta).latestGivenTime,
    //   findLatestETAWithGivenTime(orderEta).latestTime
    // );
  }

  const formatSupplierNumber = (
    supplierId: string,
    branchId: string | undefined
  ) => {
    const supplierNumber = getSupplierNumber(supplierId, branchId);
    if (!supplierNumber) return "";
    const formatted = formatMobileNumber(supplierNumber);
    return formatted !== "" ? formatted : supplierNumber;
  };

  const latestEtaResult = orderEta.length
    ? findLatestETAWithGivenTime(orderEta)
    : null;
  const latestETAInSeconds = latestEtaResult?.latestTime
    ? new Date(latestEtaResult.latestTime).getTime() / 1000
    : NaN;
  const latestETAGivenTimeInSeconds = latestEtaResult?.latestGivenTime
    ? new Date(latestEtaResult.latestGivenTime).getTime() / 1000
    : NaN;
  const isPlatformInfiniteEta =
    latestETAInSeconds === epochPlatformEtaSeconds;
  const isCountdownExpired =
    !isPlatformInfiniteEta &&
    Number.isFinite(latestETAInSeconds) &&
    latestETAInSeconds * 1000 <= Date.now();
  const showMissedEta = etaMissed || isCountdownExpired;

  return (
    <HStack
      height="auto"
      flexShrink={0}
      py={"0.4rem"}
      px={"0.5rem"}
      justifyContent={"space-between"}
      backgroundColor={"#f0f6f6"}
      boxSizing="border-box"
      style={{ margin: 0 }}
    >
      <HStack
        width={"70%"}
        spacing={6}
        height={"100%"}
        alignItems={"flex-start"}
        justifyContent={"space-between"}
      >
        <Box height={"100%"} display={"flex"} flexDirection={"column"}>
          <Text fontWeight={"bold"} fontSize={"small"} color={"#1A202C"}>
            {orderData.orderType === "SUBSCRIPTION"
              ? "SUBSCRIPTION ORDER"
              : "ORDER PLACED"}
          </Text>
          <Text fontSize={"xs"}>
            {formatDate(orderData?.orderDate ?? orderData.createdAt)}
          </Text>
          <Text fontSize={"xs"}>
            {formatTime(orderData?.orderDate ?? orderData.createdAt)}
          </Text>
          <Box marginTop={"auto"}>
            {orderStatus === OrderStatus.READY_TO_DISPATCH && (
              <Icon fontSize={"xl"}>
                <FaTruckLoading />
              </Icon>
            )}
          </Box>
        </Box>
        <Box>
          <Text fontWeight={"bold"} color={"#1A202C"}>
            SHIP TO{" "}
            {isCustomerOrderFreezed && (
              <Badge fontSize={"3xs"} colorScheme="red" height={"fit-content"}>
                BLOCKED
              </Badge>
            )}
          </Text>

          <GaraazGeneralPopoverV2
            ArekeysVisible={[false, true]}
            title={orderData?.customer?.name}
            value={[
              [preparePopverAddressObject(orderData?.shipmentAddress)],
              customerSupplierAssociations,
            ]}
            CopierKeys={["Mobile Number", "Supplier Code"]}
            titleIcon={
              <>
                <GarrazCopyText
                  text={customerDetailsToCopy}
                  popOverMessage="Customer Details Copied"
                ></GarrazCopyText>
              </>
            }
            subTitles={["Address", "Associations"]}
          >
            <PopoverTrigger>
              <Text
                fontWeight={"bold"}
                textTransform={"uppercase"}
                color="teal.600"
                cursor={"pointer"}
                fontSize={"xs"}
                display={"flex"}
                alignItems={"center"}
              >
                {orderData.customer?.name}

                <BiChevronDown
                  style={{ display: "inline", fontSize: "1.5em" }}
                />
              </Text>
            </PopoverTrigger>
          </GaraazGeneralPopoverV2>
          {(orderData?.customer?.type || orderData?.customer?.code) && (
            <HStack alignItems={"center"} spacing={1}>
              <Text fontSize={"xs"}>
                {[orderData?.customer?.type, orderData?.customer?.code]
                  .filter(Boolean)
                  .join(", ")}
              </Text>
              {orderData?.customer?.code && (
                <GarrazCopyText
                  popOverMessage="Garaaz Code Copied"
                  text={orderData.customer.code}
                />
              )}
            </HStack>
          )}
          {renderEngagementModeBadge()}
        </Box>

        <Box
          cursor={
            orderData?.isDeleted || isCustomerOrderFreezed
              ? "not-allowed"
              : "pointer"
          }
          onClick={() => {
            if (orderData?.isDeleted || isCustomerOrderFreezed) {
              return;
            }
            onOpenCptUpdate?.();
          }}
        >
          <VStack alignItems={"flex-start"} spacing={0}>
            {isPlatformInfiniteEta ? (
              <HStack alignItems="flex-start" spacing={1}>
                <Box
                  display={"flex"}
                  alignItems={"center"}
                  flexDirection={"column"}
                >
                  <Text fontWeight={"bold"} color={"#1A202C"}>
                    ETA
                  </Text>
                  <Icon fontSize={"2xl"}>
                    {" "}
                    <BiInfinite></BiInfinite>
                  </Icon>
                </Box>
                {!!orderEta?.length && (
                  <Popover
                    trigger="hover"
                    placement="bottom-start"
                    strategy="fixed"
                  >
                    <PopoverTrigger>
                      <IconButton
                        aria-label="ETA history"
                        title="ETA history"
                        icon={<InfoOutlineIcon boxSize={3} />}
                        size="xs"
                        variant="ghost"
                        minW="16px"
                        h="16px"
                        p={0}
                        mt={1}
                        color="gray.500"
                        _hover={{ color: "gray.700", bg: "transparent" }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </PopoverTrigger>
                    <Portal>
                      <PopoverContent p="0.7rem" zIndex={2000}>
                        <Text
                          fontSize={"medium"}
                          fontWeight={"bold"}
                          marginBottom={"5px"}
                          textTransform={"uppercase"}
                        >
                          ETA
                        </Text>
                        {orderEta?.map((etaObj: any, index: any) => (
                          <HStack spacing={1} key={index}>
                            <Box alignSelf={"flex-start"}>
                              <Text>{String(index + 1)}.</Text>
                            </Box>
                            <GaraazPopOverContainerStack
                              key={index}
                              etaObj={etaObj}
                              isLast={index === orderEta?.length - 1}
                            />
                          </HStack>
                        ))}
                      </PopoverContent>
                    </Portal>
                  </Popover>
                )}
              </HStack>
            ) : (
              <>
                <Box display={"flex"} alignItems={"center"}>
                  {(showMissedEta || finalOrcaStates.includes(orderStatus)) && (
                    <Text fontWeight={"bold"} color={"#1A202C"}>
                      ETA
                    </Text>
                  )}
                </Box>

                {!orderEta?.length &&
                  !startOrcaStates.includes(orderStatus) &&
                  !finalOrcaStates.includes(orderStatus) && (
                    <Box display={"flex"}>
                      {!etaProvideMissed && (
                        <GaraazCountDownClockV2
                          componentSize="medium"
                          value0={etaProvideClockValue0}
                          value100={etaProvideClockValue100}
                          titleLabel="P.ETA"
                          onCompleteTime={onEtaProvideTimeComplete}
                          isCompleted={etaProvideMissed}
                          setIsCompleted={setEtaProvideMissed}
                        ></GaraazCountDownClockV2>
                      )}

                      {etaProvideMissed && (
                        <HStack spacing={1}>
                          <FiAlertTriangle
                            color="red"
                            style={{ marginLeft: "2px" }}
                          />
                          <Text>ETA Missed</Text>
                        </HStack>
                      )}
                    </Box>
                  )}

                {!!orderEta?.length &&
                  !startOrcaStates.includes(orderStatus) &&
                  !finalOrcaStates.includes(orderStatus) &&
                  !showMissedEta && (
                    <VStack spacing={0} alignItems="center">
                      <HStack spacing="3px" alignItems="center">
                        <GaraazCountDownClockV2
                          value0={latestETAGivenTimeInSeconds}
                          value100={latestETAInSeconds}
                          componentSize="medium"
                          titleLabel="ETA"
                          onCompleteTime={onEtaTimeComplete}
                          isCompleted={etaMissed}
                          setIsCompleted={setEtaMissed}
                        ></GaraazCountDownClockV2>
                        <Popover
                          trigger="hover"
                          placement="bottom-start"
                          strategy="fixed"
                        >
                          <PopoverTrigger>
                            <IconButton
                              aria-label="ETA history"
                              title="ETA history"
                              icon={<InfoOutlineIcon boxSize={3} />}
                              size="xs"
                              variant="unstyled"
                              minW="12px"
                              w="12px"
                              h="12px"
                              p={0}
                              display="inline-flex"
                              alignItems="center"
                              justifyContent="center"
                              color="gray.500"
                              _hover={{
                                color: "gray.700",
                                bg: "transparent",
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </PopoverTrigger>
                          <Portal>
                            <PopoverContent p="0.7rem" zIndex={2000}>
                              <Text
                                fontSize={"medium"}
                                fontWeight={"bold"}
                                marginBottom={"5px"}
                                textTransform={"uppercase"}
                              >
                                ETA
                              </Text>
                              {orderEta?.map((etaObj: any, index: any) => (
                                <HStack spacing={1} key={index}>
                                  <Box alignSelf={"flex-start"}>
                                    <Text>{String(index + 1)}.</Text>
                                  </Box>
                                  <GaraazPopOverContainerStack
                                    key={index}
                                    etaObj={etaObj}
                                    isLast={index === orderEta?.length - 1}
                                  />
                                </HStack>
                              ))}
                            </PopoverContent>
                          </Portal>
                        </Popover>
                      </HStack>
                      {cptGaraazSlot ? (
                        <GaraazCptSlotLabel
                          slot={cptGaraazSlot}
                          variant="compact"
                        />
                      ) : null}
                    </VStack>
                  )}

                {(showMissedEta || finalOrcaStates.includes(orderStatus)) && (
                  <HStack spacing={1} alignItems="center">
                    <Text
                      fontSize={"xs"}
                      color={`${
                        !finalOrcaStates.includes(orderStatus)
                          ? "red"
                          : "black"
                      }`}
                    >
                      {orderEta?.length ? formatTime(latestETA) : null}
                    </Text>
                    {!!orderEta?.length && (
                      <Popover
                        trigger="hover"
                        placement="bottom-start"
                        strategy="fixed"
                      >
                        <PopoverTrigger>
                          <IconButton
                            aria-label="ETA history"
                            title="ETA history"
                            icon={<InfoOutlineIcon boxSize={3} />}
                            size="xs"
                            variant="ghost"
                            minW="16px"
                            h="16px"
                            p={0}
                            color="gray.500"
                            _hover={{ color: "gray.700", bg: "transparent" }}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </PopoverTrigger>
                        <Portal>
                          <PopoverContent p="0.7rem" zIndex={2000}>
                            <Text
                              fontSize={"medium"}
                              fontWeight={"bold"}
                              marginBottom={"5px"}
                              textTransform={"uppercase"}
                            >
                              ETA
                            </Text>
                            {orderEta?.map((etaObj: any, index: any) => (
                              <HStack spacing={1} key={index}>
                                <Box alignSelf={"flex-start"}>
                                  <Text>{String(index + 1)}.</Text>
                                </Box>
                                <GaraazPopOverContainerStack
                                  key={index}
                                  etaObj={etaObj}
                                  isLast={index === orderEta?.length - 1}
                                />
                              </HStack>
                            ))}
                          </PopoverContent>
                        </Portal>
                      </Popover>
                    )}
                  </HStack>
                )}
              </>
            )}
          </VStack>
        </Box>
        {!!activesupplierOrderStatus && (
          <Popover trigger="hover" strategy="fixed">
            <PopoverTrigger>
              <Box cursor={"pointer"}>
                <Text
                  fontWeight={"bold"}
                  fontSize={"small"}
                  color={"#1A202C"}
                  display={"flex"}
                  alignItems={"center"}
                >
                  SUPPLIER STATUS
                  <BiChevronDown
                    style={{ display: "inline", fontSize: "1.5em" }}
                  />
                </Text>
                <HStack>
                  <Text
                    fontWeight={"bold"}
                    textTransform={"uppercase"}
                    color="teal.600"
                    cursor={"pointer"}
                    fontSize={"xs"}
                    display={"flex"}
                  >
                    {getSupplierBusinessName(
                      activesupplierOrderStatus.supplierId
                    )}
                  </Text>
                  {(() => {
                    const actionIcon = activesupplierOrderStatus.actionType
                      ? ALLOCATION_ACTION_ICONS[
                          activesupplierOrderStatus.actionType
                        ]
                      : null;
                    if (!actionIcon) return null;
                    return (
                      <Tooltip
                        label={actionIcon.label}
                        hasArrow
                        placement="top"
                        shouldWrapChildren
                        bg={actionIcon.color}
                        color="white"
                      >
                        <Box
                          as="span"
                          display="inline-flex"
                          alignItems="center"
                          cursor="default"
                          onMouseEnter={(e: React.MouseEvent) =>
                            e.stopPropagation()
                          }
                        >
                          <Icon
                            as={actionIcon.icon}
                            color={actionIcon.color}
                            boxSize={4}
                          />
                        </Box>
                      </Tooltip>
                    );
                  })()}
                </HStack>
                {activesupplierOrderStatus.branchId && (
                  <Text fontSize={"3xs"}>
                    {getSupplierBranchName(
                      activesupplierOrderStatus.supplierId,
                      activesupplierOrderStatus.branchId
                    )}
                  </Text>
                )}
                <Badge
                  backgroundColor={
                    getSupplierStateColor(
                      activesupplierOrderStatus.orderStatus as any
                    )?.color
                  }
                  color={
                    getSupplierStateColor(
                      activesupplierOrderStatus.orderStatus as any
                    )?.textColor
                  }
                >
                  {removeUnderScoreWithUpperCase(
                    activesupplierOrderStatus.orderStatus
                  )}
                </Badge>
              </Box>
            </PopoverTrigger>
            <Portal>
            <PopoverContent
              padding={3}
              zIndex={2000}
              maxH="70vh"
              overflowY="auto"
            >
              <OrderedList>
                {orderData.allocatedTo.map((item: any, index: number) => {
                  return (
                    <ListItem key={index}>
                      <Box _notFirst={{ marginTop: "4" }}>
                        <Box
                          color={"#1A202C"}
                          display={"flex"}
                          alignItems={"center"}
                        >
                          <Text fontSize={"smaller"}>
                            {" "}
                            {getSupplierBusinessName(item.supplierId)}
                          </Text>
                        </Box>
                        {item.branchId && (
                          <Box>
                            <Text fontSize={"2xs"}>
                              {getSupplierBranchName(
                                item.supplierId,
                                item.branchId
                              )}
                            </Text>
                          </Box>
                        )}
                        <Text
                          fontSize={"smaller"}
                          display={"flex"}
                          alignItems={"center"}
                        >
                          {formatSupplierNumber(
                            item.supplierId,
                            item?.branchId
                          )}
                          <GarrazCopyText
                            popOverMessage="Supplier number Copied"
                            text={getNationalMobileForCopy(
                              getSupplierNumber(
                                item.supplierId,
                                item?.branchId
                              )
                            )}
                          />
                        </Text>
                        <Badge
                          backgroundColor={
                            getSupplierStateColor(item.orderStatus as any)
                              ?.color
                          }
                          color={
                            getSupplierStateColor(item.orderStatus as any)
                              ?.textColor
                          }
                          fontSize={"xx-small"}
                        >
                          {removeUnderScoreWithUpperCase(item.orderStatus)}
                        </Badge>
                      </Box>
                    </ListItem>
                  );
                })}
              </OrderedList>
            </PopoverContent>
            </Portal>
          </Popover>
        )}
      </HStack>
      <Box
        display={"flex"}
        flexDirection={"column"}
        alignItems={"flex-start"}
        height={"100%"}
        width={"36%"}
      >
        <Box
          display={"flex"}
          flexDirection={"column"}
          alignContent={"flex-end"}
          marginLeft={"auto"}
        >
          <Box
            display={"flex"}
            alignItems={"center"}
            justifyContent={"flex-end"}
            textAlign={"end"}
            marginLeft={"auto"}
          >
            {orderData.orderType === "REGULAR" && (
              <Icon mr={1} fontSize={"xl"} fontWeight={"bold"}>
                <BiCart></BiCart>
              </Icon>
            )}
            {orderData.orderType === "RETURN" && (
              <Icon mt={1}>
                <GiReturnArrow />
              </Icon>
            )}
            {orderData.orderType === "SUBSCRIPTION" && (
              <Icon mr={1} fontSize={"xl"}>
                <BiDollarCircle></BiDollarCircle>
              </Icon>
            )}
            <Text>{`ORDER # `} </Text>

            <Text
              fontSize={"12px"}
              marginLeft={"3px"}
              textAlign={"end"}
              color={"#1A202C"}
            >
              {orderData.orderType === "RETURN" ? "RT-" : ""}
              {orderNotoShow}
            </Text>

            <GarrazCopyText
              popOverMessage="Order Id Copied"
              text={orderData.orderNo}
            />
          </Box>
        </Box>

        <HStack
          spacing={2}
          justifyContent="flex-end"
          width="100%"
          marginTop="auto"
          alignItems="center"
          flexWrap="nowrap"
        >
          {transactionType && (
            <Badge
              backgroundColor={"white"}
              fontSize={"xx-small"}
              height="fit-content"
              outline={"1px solid green"}
            >
              {transactionType === "Facilitation" ? "F" : "T"}
            </Badge>
          )}

          {orderData.orderType === "REGULAR" && isFreeDelivery && (
            <Badge
              fontSize="xx-small"
              height="fit-content"
              outline="0.1px solid grey"
            >
              FREE
            </Badge>
          )}

          {(orderData?.isVehicleInfoExtractedByOpenAI ||
            orderData?.isOrderItemsExtractedByOpenAI) && (
              <Badge
                background={"linear-gradient(to right, #4776e6, #8e54e9)"}
                color={"white"}
                fontSize={"xx-small"}
                height="fit-content"
                display={"flex"}
                alignItems={"center"}
              >
                PHANTOM
              </Badge>
            )}

          {isCarIconVisible && (
            <Link
              as={NavLink}
              to={paths.cars}
              aria-label="Car catalogue"
              state={{
                orderId: orderData._id,
                orderNo: orderData.orderNo,
                scrollPosition:
                  window.scrollY || document.documentElement.scrollTop,
              }}
              display="inline-flex"
              alignItems="center"
              justifyContent="center"
              lineHeight={1}
              flexShrink={0}
              onClick={() => {
                setScrollOrderId(orderData._id);
              }}
            >
              <Icon as={FaCarSide} boxSize="14px" aria-hidden />
            </Link>
          )}
          {orderStatus === OrderStatus.CUSTOMER_HOLD && (
            <Icon as={AiOutlineStop} color={"red"} boxSize="14px" />
          )}
        </HStack>
      </Box>
    </HStack>
  );
};

export default GaraazOrderComponentHeader;
